"use client"

import {
  Box,
  createListCollection,
  HStack,
  Icon,
  IconButton,
  Portal,
  Select,
  Skeleton,
  SkeletonText,
  Text,
  useSelectContext,
  VStack,
} from "@chakra-ui/react"
import { useEffect, useMemo, useState } from "react"

import { useAuth } from "@/contexts/AuthContext"
import { getComments } from "@/lib/SingleArticleComments"
import { Comment } from "@/types/database"

import CommentItem from "./CommentItem"
import {
  CabinetIcon,
  EyeRegularIcon,
  LikeOutlineIcon,
  SortDescIcon,
} from "@/components/icons"

type SortOption = "newest" | "oldest" | "most_replies" | "most_likes"

interface CommentListProps {
  postId: string
  refreshTrigger?: number
  onCommentCountChange?: (count: number) => void
}

const SelectSortTrigger = () => {
  const select = useSelectContext()
  const items: Array<{ label: string; icon: React.ReactNode } | undefined> =
    select.selectedItems
  const label = items[0]?.label
  const icon = items[0]?.icon

  return (
    <IconButton px="4" size="sm" variant="subtle" {...select.getTriggerProps()}>
      {select.hasSelectedItems ? icon : <SortDescIcon aria-label={label} />}
    </IconButton>
  )
}

const CommentList = ({
  onCommentCountChange,
  postId,
  refreshTrigger,
}: CommentListProps) => {
  const { session } = useAuth()
  const [comments, setComments] = useState<Comment[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [sortBy, setSortBy] = useState<SortOption>("newest")
  const [likedComments, setLikedComments] = useState<Set<string>>(new Set())

  // Count total comments including replies
  const getTotalCommentCount = (comments: Comment[]): number => {
    return comments.reduce((total, comment) => {
      return (
        total +
        1 +
        (comment.replies ? getTotalCommentCount(comment.replies) : 0)
      )
    }, 0)
  }

  // Sort comments based on selected option
  const sortComments = (
    comments: Comment[],
    sortOption: SortOption
  ): Comment[] => {
    const sorted = comments.toSorted((a, b) => {
      switch (sortOption) {
        case "newest":
          return (
            new Date(b.date_created).getTime() -
            new Date(a.date_created).getTime()
          )
        case "oldest":
          return (
            new Date(a.date_created).getTime() -
            new Date(b.date_created).getTime()
          )
        case "most_replies":
          const aReplies = a.replies?.length || 0
          const bReplies = b.replies?.length || 0
          return bReplies - aReplies
        case "most_likes":
          const aLikes = Array.isArray(a.likes) ? a.likes.length : 0
          const bLikes = Array.isArray(b.likes) ? b.likes.length : 0
          return bLikes - aLikes
        default:
          return 0
      }
    })

    // Also sort replies within each comment
    return sorted.map((comment) => ({
      ...comment,
      replies: comment.replies
        ? sortComments(comment.replies, sortOption)
        : undefined,
    }))
  }

  const sortedComments = useMemo(
    () => sortComments(comments, sortBy),
    [comments, sortBy]
  )

  // Check which comments the current user has liked
  const checkLikedComments = (
    comments: Comment[],
    userEmail: string
  ): Set<string> => {
    const liked = new Set<string>()

    const checkComment = (comment: Comment) => {
      if (Array.isArray(comment.likes)) {
        const hasLiked = comment.likes.some(
          (like) => like.user_email === userEmail
        )
        if (hasLiked) {
          liked.add(comment.id)
        }
      }

      // Check replies recursively
      if (comment.replies) {
        comment.replies.forEach(checkComment)
      }
    }

    comments.forEach(checkComment)
    return liked
  }

  const fetchComments = async () => {
    setIsLoading(true)
    try {
      const fetchedComments = await getComments({ postId })
      setComments(fetchedComments)

      // Check which comments the current user has liked
      if (session?.user?.email) {
        const liked = checkLikedComments(fetchedComments, session.user.email)
        setLikedComments(liked)
      } else {
        setLikedComments(new Set())
      }

      // Calculate and notify parent of comment count change
      const totalCount = getTotalCommentCount(fetchedComments)
      onCommentCountChange?.(totalCount)
    } catch (error) {
      console.error("Error fetching comments:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // Update liked comments when session changes
  useEffect(() => {
    if (session?.user?.email && comments.length > 0) {
      const liked = checkLikedComments(comments, session.user.email)
      setLikedComments(liked)
    } else {
      setLikedComments(new Set())
    }
  }, [session?.user?.email, comments])

  useEffect(() => {
    fetchComments()
  }, [postId, refreshTrigger])

  if (isLoading) {
    return (
      <VStack align="stretch" gap={4}>
        {["comment-skeleton-1", "comment-skeleton-2", "comment-skeleton-3"].map(
          (skeletonKey) => (
            <Box borderRadius="md" borderWidth={1} key={skeletonKey} p={4}>
              <HStack gap={3} mb={3}>
                <Skeleton borderRadius="full" height="40px" width="40px" />
                <VStack align="start" gap={1}>
                  <Skeleton height="16px" width="120px" />
                  <Skeleton height="12px" width="80px" />
                </VStack>
              </HStack>
              <SkeletonText gap="2" noOfLines={3} />
            </Box>
          )
        )}
      </VStack>
    )
  }

  if (comments.length === 0) {
    return (
      <Box
        borderTop={"1px solid {colors.border.muted}"}
        py={8}
        textAlign="center"
        w="full"
      >
        <Text color="fg.muted">
          No comments yet. Be the first to share your thoughts!
        </Text>
      </Box>
    )
  }

  const totalComments = getTotalCommentCount(comments)

  const filter_sorting_type = createListCollection({
    items: [
      {
        icon: <Icon as={SortDescIcon} size="md" />,
        label: "Newest",
        value: "newest",
      },
      {
        icon: <Icon as={CabinetIcon} size="md" />,
        label: "Oldest",
        value: "oldest",
      },
      {
        icon: <Icon as={EyeRegularIcon} size="md" />,
        label: "Most Replied",
        value: "most_replies",
      },
      {
        icon: <Icon as={LikeOutlineIcon} size="md" />,
        label: "Most Liked",
        value: "most_likes",
      },
    ],
  })
  return (
    <VStack align="stretch" gap={4} pb={8} pt={6} px={8}>
      <HStack align="center" justify="space-between">
        <Text fontSize="xl" fontWeight="bold">
          Responses ({totalComments})
        </Text>

        <HStack gap={2} justifyContent={"flex-end"}>
          <Text color="fg.muted" flexShrink={0} fontSize="sm">
            Sort by:
          </Text>
          <Select.Root
            collection={filter_sorting_type}
            defaultValue={["newest"]}
            onValueChange={(e) => setSortBy(e.value[0] as SortOption)}
            positioning={{ sameWidth: false }}
          >
            <Select.HiddenSelect />
            <Select.Control>
              <SelectSortTrigger />
            </Select.Control>
            <Portal>
              <Select.Positioner>
                <Select.Content minW="32">
                  {filter_sorting_type.items.map((sorting_item) => (
                    <Select.Item item={sorting_item} key={sorting_item.value}>
                      <HStack color="fg.muted">
                        {sorting_item.icon}
                        <Text>{sorting_item.label}</Text>
                      </HStack>
                      <Select.ItemIndicator />
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Portal>
          </Select.Root>
        </HStack>
      </HStack>
      <VStack
        alignItems="stretch"
        // css={{
        //   '& :first-child .response': {
        //     borderBottomEndRadius: 'xl',
        //     borderBottomStartRadius: 'xl',
        //   },
        //   '& :last-child .response': {
        //     borderTopEndRadius: 'xl',
        //     borderTopStartRadius: 'xl',
        //   },
        // }}
        gap={1}
      >
        {sortedComments.map((comment) => (
          <CommentItem
            comment={comment}
            key={comment.id}
            likedComments={likedComments}
            onCommentSubmitted={fetchComments}
            onLikeUpdate={(commentId: string, isLiked: boolean) => {
              setLikedComments((prev) => {
                const newSet = new Set(prev)
                if (isLiked) {
                  newSet.add(commentId)
                } else {
                  newSet.delete(commentId)
                }
                return newSet
              })
            }}
            postId={postId}
          />
        ))}
      </VStack>
    </VStack>
  )
}

export default CommentList
